import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaThumbsUp, FaCommentAlt, FaTrash } from 'react-icons/fa';
import axios from 'axios';

const API_URL = "https://api.lii.com.co:5101/api/family-stories";

const FamilyStories = () => {
  const [stories, setStories] = useState([]);
  const [newStory, setNewStory] = useState({ title: '', description: '' });
  const [newComment, setNewComment] = useState({});
  const [activeMenu, setActiveMenu] = useState(null);
  const [activeCommentMenu, setActiveCommentMenu] = useState(null);
  const [postIdForComment, setPostIdForComment] = useState(null);

  // Fetch stories from backend
  useEffect(() => {
    axios.get(API_URL)
      .then(response => setStories(response.data))
      .catch(error => console.error('Error fetching stories:', error));
  }, []);

  // Handle input change
  const handleInputChange = (e) => {
    setNewStory({ ...newStory, [e.target.name]: e.target.value });
  };

  // Add a new story
  const handleAddStory = async () => {
    if (!newStory.title || !newStory.description) return;

    try {
      const response = await axios.post(API_URL, newStory);
      setStories([response.data, ...stories]);
      setNewStory({ title: '', description: '' });
    } catch (error) {
      console.error('Error adding story:', error);
    }
  };

  // Delete a story
  const handleDeleteStory = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`);
      setStories(stories.filter(story => story._id !== id));
    } catch (error) {
      console.error('Error deleting story:', error);
    }
  };

  // Like a story
  const handleLikeStory = async (id) => {
    try {
      const response = await axios.post(`${API_URL}/${id}/like`);
      setStories(stories.map(story => (story._id === id ? response.data : story)));
    } catch (error) {
      console.error('Error liking story:', error);
    }
  };

  // Add a comment
  const handleAddComment = async (id) => {
    if (!newComment[id]?.trim()) return;

    try {
      const response = await axios.post(`${API_URL}/${id}/comment`, { text: newComment[id] });
      setStories(stories.map(story => (story._id === id ? response.data : story)));
      setNewComment({ ...newComment, [id]: '' });
      setPostIdForComment(null);
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  // Delete a comment
  const handleDeleteComment = async (storyId, commentId) => {
    try {
      await axios.delete(`${API_URL}/${storyId}/comment/${commentId}`);
      setStories(stories.map(story =>
        story._id === storyId
          ? { ...story, comments: story.comments.filter(comment => comment._id !== commentId) }
          : story
      ));
      setActiveCommentMenu(null);
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  // Toggle comment menu for deletion
  const toggleCommentMenu = (commentId) => {
    setActiveCommentMenu(activeCommentMenu === commentId ? null : commentId);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-blue-600 text-white py-4 shadow-md">
        <div className="container mx-auto flex items-center px-6">
          <h1 className="text-2xl font-bold">
            <Link to="/dashboard" className="text-white hover:text-gray-300">lii</Link>
          </h1>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg mt-8">
        <h1 className="text-3xl font-bold mb-6 text-center text-blue-600">Anecdotes</h1>

        {/* Add Story Form */}
        <div className="mb-6">
          <input
            type="text"
            name="title"
            placeholder="Titre"
            value={newStory.title}
            onChange={handleInputChange}
            className="border p-3 rounded-lg w-full mb-4 focus:ring-2 focus:ring-blue-400 focus:outline-none"
          />
          <textarea
            name="description"
            placeholder="Écrivez une anecdote..."
            value={newStory.description}
            onChange={handleInputChange}
            className="border p-3 rounded-lg w-full mb-4 h-32 focus:ring-2 focus:ring-blue-400 focus:outline-none"
          />
          <button
            onClick={handleAddStory}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg shadow-md hover:bg-blue-600 transition w-full"
          >
            ➕ Ajouter à la collection
          </button>
        </div>

        {/* Story List */}
        <div className="space-y-4">
          {stories.map(story => (
            <div key={story._id} className="relative p-4 border rounded-lg shadow-sm bg-gray-100">
              
              {/* Menu for deleting story */}
              <div className="absolute top-2 right-2">
                <button
                  onClick={() => setActiveMenu(activeMenu === story._id ? null : story._id)}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  title="Options"
                >
                  &#x2026;
                </button>
                {activeMenu === story._id && (
                  <div className="absolute top-8 right-0 bg-white border shadow rounded z-50">
                    <ul>
                      <li
                        onClick={() => handleDeleteStory(story._id)}
                        className="px-4 py-2 text-sm text-red-600 hover:bg-gray-100 cursor-pointer"
                      >
                        Delete Story
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              {/* Story Content */}
              <h2 className="text-xl font-bold text-blue-600">{story.title}</h2>
              <p className="text-gray-700 mt-2">{story.description}</p>

              {/* Like & Comment Buttons */}
              <div className="flex justify-center gap-8 mt-4">
                <button
                  onClick={() => handleLikeStory(story._id)}
                  className="flex items-center gap-2 text-gray-600 hover:text-blue-600"
                >
                  <FaThumbsUp /> Like {story.likes}
                </button>
                <button
                  onClick={() => setPostIdForComment(story._id)}
                  className="flex items-center gap-2 text-gray-600 hover:text-blue-600"
                >
                  <FaCommentAlt /> Comment
                </button>
              </div>

              {/* Comment Input */}
              {postIdForComment === story._id && (
                <div className="mt-4">
                  <textarea
                    value={newComment[story._id] || ''}
                    onChange={(e) => setNewComment({ ...newComment, [story._id]: e.target.value })}
                    className="w-full p-2 border rounded mb-2"
                    placeholder="Add a comment..."
                  />
                  <button
                    onClick={() => handleAddComment(story._id)}
                    className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"
                  >
                    Add Comment
                  </button>
                </div>
              )}

              {/* Comment List */}
              <div className="mt-4">
                {story.comments?.map(comment => (
                  <div key={comment._id} className="relative flex justify-between items-center mb-2 p-2 bg-gray-100 rounded">
                    <p className="text-gray-600 text-sm">💬 {comment.text}</p>

                    {/* Comment Delete Menu */}
                    <div className="relative">
                      <button
                        onClick={() => toggleCommentMenu(comment._id)}
                        className="text-gray-400 hover:text-gray-600 focus:outline-none"
                      >
                        &#x2026;
                      </button>
                      {activeCommentMenu === comment._id && (
                        <div className="absolute top-6 right-0 bg-white border shadow rounded z-50">
                          <ul>
                            <li
                              onClick={() => handleDeleteComment(story._id, comment._id)}
                              className="px-4 py-2 text-sm text-red-600 hover:bg-gray-100 cursor-pointer"
                            >
                              Delete Comment
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FamilyStories;